import React, { useEffect, useState } from 'react';
import { useGuardedGenerate } from '../../hooks/useGuardedGenerate';
import { DocumentSendSection, DocumentSendSource } from '../documents/DocumentSendSection';
import { ActivityIndicator, Linking, Platform, ScrollView, StyleSheet, Switch, Text, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import { overlayBackdropStyle, overlayCardLook } from '../OverlayPanel';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { InsetModal as Modal } from '../InsetModal';
import { colors, spacing, typography } from '../../theme';
import { useLocale } from '../../contexts/LocaleContext';
import { useRTLStyles } from '../../hooks/useRTLStyles';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { useAppStore } from '../../store/appStore';
import type { PackingSlipOptions, PackingSlipResult } from '../../services/api';
import { DocumentTemplatePicker } from '../documents/DocumentTemplatePicker';
import type { DocumentLanguageChoice } from '../../utils/documentTypes';

interface Props {
  visible: boolean;
  /** What will be printed, e.g. "3 selected orders" / "All 12 orders matching the filters". */
  scopeLabel: string;
  /** Offer the pick-list switch (only meaningful for several orders). */
  multipleOrders?: boolean;
  /** Where "Send to vendor" goes. */
  vendor?: { name?: string; phone?: string; email?: string };
  onGenerate: (options: PackingSlipOptions) => Promise<PackingSlipResult>;
  onClose: () => void;
  /** What the slips are about (the vendor, or one order) — enables "Send on WhatsApp" with a message template. */
  sendSource?: DocumentSendSource;
}

export function openFileUrl(url: string) {
  if (Platform.OS === 'web') window.open(url, '_blank', 'noopener');
  else Linking.openURL(url).catch(() => {});
}

/** Packing slip options → generate the PDF → open it or send its link to the vendor. */
export const PackingSlipModal: React.FC<Props> = ({ visible, scopeLabel, multipleOrders, vendor, onGenerate, onClose, sendSource }) => {
  const styles = useThemedStyles(getStyles);
  const { t, locale } = useLocale();
  const rtl = useRTLStyles();
  const insets = useSafeAreaInsets();
  const { height } = useWindowDimensions();
  const businessId = useAppStore(s => String(s.currentBusiness?._id || (s.currentBusiness as any)?.id || ''));
  const [options, setOptions] = useState<PackingSlipOptions>({});
  const [language, setLanguage] = useState<DocumentLanguageChoice>('template');
  const [templatesReady, setTemplatesReady] = useState(false);
  const { busy, error, result, setResult, run } = useGuardedGenerate<PackingSlipResult>([visible, scopeLabel, businessId], t('vendors.slips.failed'));

  useEffect(() => {
    if (!visible) return;
    setOptions({ showPrices: false, hideCustomerPhone: false, pickList: true });
    setLanguage('template');
    setTemplatesReady(false);
  }, [visible, scopeLabel, businessId]);

  const generate = () => {
    if (!templatesReady) return;
    const resolved = language === 'template' ? undefined : language === 'auto' ? (/^ar/.test(String(locale || '')) ? 'ar' : 'en') : language;
    return run(() => onGenerate({ ...options, ...(resolved ? { locale: resolved } : {}) }));
  };

  const message = result ? t('vendors.slips.shareText', { name: vendor?.name || '', url: result.url }) : '';
  const phone = String(vendor?.phone || '').replace(/[^\d]/g, '');

  const toggle = (key: keyof PackingSlipOptions, label: string, hint?: string) => (
    <View style={[styles.toggleRow, { flexDirection: rtl.rowDirection }]}>
      <View style={styles.toggleText}>
        <Text style={[styles.toggleLabel, { textAlign: rtl.textStart }]}>{label}</Text>
        {!!hint && <Text style={[styles.toggleHint, { textAlign: rtl.textStart }]}>{hint}</Text>}
      </View>
      <Switch disabled={busy} value={!!options[key]} onValueChange={(v) => setOptions((p) => ({ ...p, [key]: v }))} />
    </View>
  );

  const action = (icon: string, label: string, onPress: () => void, primary?: boolean) => (
    <TouchableOpacity key={label} style={[styles.actionButton, { flexDirection: rtl.rowDirection }, primary && styles.actionPrimary]} onPress={onPress} activeOpacity={0.8}>
      <Ionicons name={icon as any} size={18} color={primary ? (colors.white as string) : (colors.primary as string)} />
      <Text style={[styles.actionText, primary && styles.actionTextPrimary]}>{label}</Text>
    </TouchableOpacity>
  );

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={[styles.backdrop, { paddingTop: Math.max(spacing.lg, insets.top), paddingBottom: Math.max(spacing.lg, insets.bottom) }]}>
        <View style={[styles.card, { maxHeight: height - Math.max(spacing.lg, insets.top) - Math.max(spacing.lg, insets.bottom) }]}>
          <View style={[styles.header, { flexDirection: rtl.rowDirection }]}>
            <Ionicons name="document-text-outline" size={20} color={colors.primary as string} />
            <Text style={[styles.title, { textAlign: rtl.textStart }]}>{t('vendors.slips.title')}</Text>
            <TouchableOpacity onPress={onClose} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} accessibilityLabel={t('vendors.filters.close')}>
              <Ionicons name="close" size={22} color={colors.text.secondary as string} />
            </TouchableOpacity>
          </View>
          <Text style={[styles.scope, { textAlign: rtl.textStart }]}>{scopeLabel}</Text>

          {!result ? (
            <ScrollView style={{ flexShrink: 1 }} contentContainerStyle={{ gap: spacing.sm }}>
              <DocumentTemplatePicker
                key={`${scopeLabel}:${visible}`}
                type="packing_slip"
                templateId={options.templateId}
                onTemplate={(templateId) => setOptions((p) => ({ ...p, templateId }))}
                language={language}
                onLanguage={setLanguage}
                onReadyChange={setTemplatesReady}
                disabled={busy}
              />
              {multipleOrders && toggle('pickList', t('vendors.slips.pickList'), t('vendors.slips.pickListHint'))}
              {toggle('showPrices', t('vendors.slips.showPrices'), t('vendors.slips.showPricesHint'))}
              {toggle('hideCustomerPhone', t('vendors.slips.hidePhone'))}
              {!!error && <Text style={[styles.error, { textAlign: rtl.textStart }]}>{error}</Text>}
            </ScrollView>
          ) : (
            <ScrollView style={{ flexShrink: 1 }} contentContainerStyle={{ gap: spacing.sm }}>
              <View style={[styles.done, { flexDirection: rtl.rowDirection }]}>
                <Ionicons name="checkmark-circle" size={22} color={colors.success as string} />
                <Text style={[styles.doneText, { textAlign: rtl.textStart }]}>
                  {t('vendors.slips.ready', { count: result.slips })}{result.truncated ? ` ${t('vendors.slips.truncated', { count: result.orders })}` : ''}
                </Text>
              </View>
              <View style={styles.actions}>
                {action('open-outline', t('vendors.slips.open'), () => openFileUrl(result.url), true)}
                {!!vendor?.email && action('mail-outline', t('vendors.slips.sendEmail'), () => Linking.openURL(`mailto:${vendor.email}?subject=${encodeURIComponent(t('vendors.slips.emailSubject'))}&body=${encodeURIComponent(message)}`).catch(() => {}))}
              </View>
              {!!sendSource && (
                <DocumentSendSection
                  type="packing_slip"
                  source={sendSource}
                  document={{ url: result.url, fileName: result.fileName, orders: result.orders }}
                  recipient={{ name: vendor?.name, phone: vendor?.phone }}
                  language={language === 'template' || language === 'auto' ? undefined : language}
                  onLeave={onClose}
                />
              )}
            </ScrollView>
          )}
          {!result && (
            <TouchableOpacity accessibilityRole="button" style={[styles.generate, (busy || !templatesReady) && styles.disabled]} onPress={generate} disabled={busy || !templatesReady}>
              {busy ? <ActivityIndicator color={colors.white as string} /> : <Text style={styles.generateText}>{t('vendors.slips.generate')}</Text>}
            </TouchableOpacity>
          )}
        </View>
      </View>
    </Modal>
  );
};

const getStyles = () => StyleSheet.create({
  backdrop: { flex: 1, ...overlayBackdropStyle(), justifyContent: 'center', alignItems: 'center', padding: spacing.lg },
  card: { width: '100%', maxWidth: 460, backgroundColor: colors.background.surface, borderRadius: spacing.borderRadius.lg, padding: spacing.lg, gap: spacing.sm, ...overlayCardLook() },
  header: { alignItems: 'center', gap: spacing.sm },
  title: { flex: 1, fontSize: typography.fontSize.lg, fontWeight: '700', color: colors.text.primary },
  scope: { fontSize: typography.fontSize.sm, color: colors.text.secondary, marginBottom: spacing.xs },
  langRow: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.xs },
  langChip: { paddingVertical: 5, paddingHorizontal: spacing.md, borderRadius: 999, borderWidth: 1, borderColor: colors.border },
  langChipActive: { backgroundColor: colors.primary, borderColor: colors.primary },
  langText: { fontSize: typography.fontSize.xs, color: colors.text.primary, fontWeight: '600' },
  langTextActive: { color: colors.white },
  toggleRow: { alignItems: 'center', justifyContent: 'space-between', gap: spacing.md, paddingVertical: spacing.xs },
  toggleText: { flex: 1 },
  toggleLabel: { fontSize: typography.fontSize.sm, color: colors.text.primary, fontWeight: '600' },
  toggleHint: { fontSize: typography.fontSize.xs, color: colors.text.secondary, marginTop: 2 },
  error: { color: colors.error, fontSize: typography.fontSize.sm },
  generate: { marginTop: spacing.sm, backgroundColor: colors.primary, borderRadius: spacing.borderRadius.md, paddingVertical: spacing.md, alignItems: 'center' },
  generateText: { color: colors.white, fontSize: typography.fontSize.base, fontWeight: '700' },
  disabled: { opacity: 0.6 },
  done: { alignItems: 'center', gap: spacing.sm, paddingVertical: spacing.sm },
  doneText: { flex: 1, fontSize: typography.fontSize.sm, color: colors.text.primary },
  actions: { gap: spacing.sm },
  actionButton: { alignItems: 'center', justifyContent: 'center', gap: spacing.sm, paddingVertical: spacing.sm + 2, borderRadius: spacing.borderRadius.md, borderWidth: 1, borderColor: colors.primary },
  actionPrimary: { backgroundColor: colors.primary },
  actionText: { fontSize: typography.fontSize.sm, color: colors.primary, fontWeight: '700' },
  actionTextPrimary: { color: colors.white },
});
